/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Card, Form, Input, Select, Switch } from "core/components";
import { AllocationRuleFormData, AllocationRuleSchema } from "core/schema";
import { admin_allocation_rule_path, admin_allocation_rules_path } from "routes";
import { navigateTo } from "core/navigation";

type AllocationRuleFormProps = {
  allocation_rule: AllocationRuleFormData;
};

const AllocationRuleForm = ({ allocation_rule, accounts }: AllocationRuleFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<AllocationRuleFormData>({
    mode: "onChange",
    defaultValues: AllocationRuleSchema.cast(allocation_rule),
    resolver: yupResolver(AllocationRuleSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);

  const save = async (data: AllocationRuleFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_allocation_rule_path(data), data);
      } else {
        await api.post(admin_allocation_rules_path(), data);
      }

      navigateTo(admin_allocation_rules_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header title={allocation_rule.id ? "Edit Allocation Rule" : "New Allocation Rule"}>
            <Button
              icon="arrow-left"
              iconProps={{ className: "mr-3" }}
              className="button--default mr-3"
              disabled={isSubmitting}
              title="Back"
              onClick={() => navigateTo(admin_allocation_rules_path())}
            />
            <Button
              icon="save"
              iconProps={{ className: "mr-3" }}
              className="button--brand mr-3"
              disabled={isSubmitting}
              title="Save"
              onClick={handleSubmit(async (data) => save(data))}
            />
          </Card.Header>
          <Card.Body>
            <Container>
              <Row>
                <Col lg={3} />
                <Col>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                      />
                    )}
                  />
                  <Input
                    {...register("code")}
                    className="field text-uppercase"
                    label="Code"
                    required
                    placeholder="HIP"
                    errorMessage={errors.code?.message}
                    maxLength={20}
                  />
                  <Input {...register("name")} label="Name" required placeholder="HIP" errorMessage={errors.name?.message} maxLength={100} />
                  <Controller
                    name="account_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Default GL Account"
                        placeholder="Select GL Account"
                        required
                        options={accounts}
                        getOptionLabel={(option) => `${option.code} - ${option.name}`}
                        getOptionValue={(option) => option.id}
                        isOptionDisabled={(option) => !option.active}
                        noOptionsMessage={() => "No GL Accounts defined."}
                        onChange={(newOption) => {
                          field.onChange(newOption?.id);
                        }}
                        value={accounts.find((account) => account.id == field.value) || null}
                        errorMessage={errors.account_id?.message}
                      />
                    )}
                  />
                </Col>
                <Col lg={3} />
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
    </React.StrictMode>
  );
};

export default AllocationRuleForm;
